/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { HFlow, Text, Theme, useTheme } from 'bold-ui'
import { ColorScale } from 'bold-ui/lib/styles/colors'
import { RadioFieldContainer } from 'components/form/field/RadioFieldContainer'
import { cloneElement, ReactElement } from 'react'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { TipoCompartilhamentoCuidadoPlanoEnum } from '../model-cuidadocompartilhado'

interface TipoCompartilhamentoRadioFieldProps {
  name: MetaPath<TipoCompartilhamentoCuidadoPlanoEnum>
  value: TipoCompartilhamentoCuidadoPlanoEnum
  disabled?: boolean
  text: string
  color: ColorScale
  backgroundColor: string
  icon: ReactElement
  size: 'small' | 'large'
}

export function TipoCompartilhamentoRadioField(props: Readonly<TipoCompartilhamentoRadioFieldProps>) {
  const { name, value, disabled, text, color, backgroundColor, icon, size } = props

  const {
    input: { value: fieldValue },
  } = useField<TipoCompartilhamentoCuidadoPlanoEnum>(name.absolutePath(), { subscription: { value: true } })

  const isAtivo = value === fieldValue

  const theme = useTheme()
  const classes = getStyles(theme, color, isAtivo, backgroundColor, size, disabled)

  const iconSize = size === 'small' ? '1.5rem' : '2.25rem'

  return (
    <RadioFieldContainer name={name} value={value} style={classes.button} disabled={disabled} isRadioHidden>
      <HFlow hSpacing={0.5} alignItems='center'>
        <span css={classes.icon}> {cloneElement(icon, { width: iconSize, height: iconSize })}</span>
        <Text fontWeight='bold' style={classes.text}>
          {text}
        </Text>
      </HFlow>
    </RadioFieldContainer>
  )
}

const getStyles = (
  theme: Theme,
  color: ColorScale,
  isAtivo: boolean,
  backgroundColor: string,
  size: 'small' | 'large',
  disabled?: boolean
) => {
  const borderColor = disabled ? color.c80 : color.c40
  const border = isAtivo ? '0' : `1px solid ${borderColor}`
  const backgroundColorFinal = isAtivo ? backgroundColor : theme.pallete.gray.c100
  const hoverBackground = isAtivo ? backgroundColor : color.c100
  const textColorWhenEnabled = isAtivo ? theme.pallete.gray.c100 : color.c40
  const textColor = disabled ? color.c80 : textColorWhenEnabled
  const width = size === 'small' ? '12.75rem' : '13.5rem'
  const height = size === 'small' ? '2.5rem' : '3.25rem'

  return {
    button: css`
      width: ${width};
      height: ${height};
      padding: 0;
      min-inline-size: max-content;
      align-content: center;
      border: ${border};
      border-radius: 2px;
      background-color: ${backgroundColorFinal};

      &:not(:disabled):hover {
        background-color: ${hoverBackground};
      }
    `,
    text: css`
      color: ${textColor};
    `,
    icon: css`
      display: flex;
      align-items: center;
    `,
  }
}
